import { ref, computed, onMounted } from 'vue';
import { searchService } from '@/api/search.service';
import { productService } from '@/api/product.service';
import { resourcesService } from '@/api/resources.service';
import type { Praeparat } from '@/types/otc/praeparat';
import type { OtcKategorie } from '@/types/otc/otcKategorie';
import { DataTransformer } from '@/api/normalizeProperties';


export function useProductOverview() {
    // Basis-Daten
    const allProducts = ref<Praeparat[]>([]);
    const displayedProducts = ref<Praeparat[]>([]);
    const categories = ref<OtcKategorie[]>([]);
    const excludedIds = ref<number[]>([]);

    // Zustände
    const loading = ref(false);
    const error = ref<unknown>(null);
    const selectedLetter = ref<string>('');
    const searchKeyword = ref<string>('');

    // Konfiguration
    const INITIAL_COUNT = 9;
    const LOAD_MORE_COUNT = 3;

    // Computed Properties
    const totalCount = computed(() => allProducts.value.length);
    const currentCount = computed(() => displayedProducts.value.length);

    const letterIndex = computed(() =>
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => ({
            letter,
            hasItems: allProducts.value.some(p => p.name?.startsWith(letter))
        }))
    );

    const filteredByLetter = computed(() => {
        if (!selectedLetter.value) return displayedProducts.value;
        return allProducts.value.filter(p => p.name?.startsWith(selectedLetter.value));
    });


    // Produkte laden
    const loadProducts = async ():Promise<boolean> => {
        loading.value = true;
        error.value = null;

        try {
            const [excluded, raw, imageApi, cats] = await Promise.all([
                productService.getExcludedOtcs(),
                searchService.otcCategorySearch(),
                resourcesService.publicImageApiUrl(),
                productService.getAllOtcCategories()
            ]);
            console.log('API response', {
                excluded: excluded?.length || 0,
                raw: raw,
                imageApi: imageApi,
                categories: cats?.length || 0
            });

            
            excludedIds.value = excluded || [];
            categories.value = cats || [];
            const baseUrl = imageApi + '?key=';

   
            let hits: any[] = [];
            if (Array.isArray(raw)) {
                hits = raw;
            } else if (raw?.HitsMetadata?.Hits) {
                hits = raw.HitsMetadata.Hits;
            }

            console.log('Hits:', hits);


            const list = hits
                .map(h => {
                    const rawPraeparat = h.values;
                    // Debug: Originaldaten anzeigen
                    console.log('Raw praeparat:', rawPraeparat);

                    // Normalisieren
                    const normalized = DataTransformer.normalizePraeparat(rawPraeparat);  
                    return normalized;
                })
                .filter(p => !excludedIds.value.includes(p.id))
                .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

            list.forEach(p => {
                const anyP = p as any;
                if (anyP.bildKey) {
                    anyP.bildUrl = `${baseUrl}${anyP.bildKey}`;
                }
            });

            console.log('Products', list);

    
            allProducts.value = list;
            displayedProducts.value = list.slice(0, INITIAL_COUNT);

            console.log('Status:', {
                allProducts: allProducts.value.length,
                displayedProducts: displayedProducts.value.length,
                hasMore: displayedProducts.value.length < allProducts.value.length
            });
            return true;

        } catch (e) {
            console.error('Fehler beim Laden:', e);
            error.value = e;
            return false;
        } finally {
            loading.value = false;
            return false;
        }
    };
    
    const loadMore = (count: number = LOAD_MORE_COUNT) => {
           if (searchKeyword.value || selectedLetter.value) {
            console.log('Mehr laden deaktiviert bei Suche/Filter');
            return;
        }

        const currentVisible = displayedProducts.value.length;
        const next = allProducts.value.slice(currentVisible, currentVisible + count);

        if (next.length > 0) {
            displayedProducts.value.push(...next);
            console.log('Mehr Produkte geladen:', {
                added: next.length,
                totalVisible: displayedProducts.value.length,
                remaining: allProducts.value.length - displayedProducts.value.length
            });
        }
    };

 
    const setLetter = (letter: string) => {
        console.log('Buchstabe gewählt:', letter);
        selectedLetter.value = letter;
        searchKeyword.value = '';        
        displayedProducts.value = allProducts.value.filter(p =>
            p.name?.startsWith(letter)
        );
    };


    const setSearch = async (keyword: string) => {
        console.log('Suche nach:', keyword);
        searchKeyword.value = keyword;
        selectedLetter.value = '';

        if (!keyword.trim()) {
            if(!allProducts || !allProducts.value || allProducts.value.length<=0){
                await loadProducts();
            }
            displayedProducts.value = allProducts.value.slice(0, INITIAL_COUNT);
            return;
        }

        try {
            loading.value = true;
            const res = await searchService.praeparateFullText(keyword,0,-1);
            const searchResults = (res.results as any[])
                .map(h => {
                    const rawPraeparat = h;
                    const normalized = DataTransformer.normalizePraeparat(rawPraeparat);
                    return normalized;
                })
                .filter(p => !excludedIds.value.includes(p.id));
            displayedProducts.value = searchResults;
            console.log('Suchergebnisse:', searchResults.length);

        } catch (err) {
            console.error('Such-Fehler:', err);
            error.value = err;
            displayedProducts.value = [];
        } finally {
            loading.value = false;
        }
    };


    const resetToDefault = async() => {
        console.log('Reset auf Standard');
        searchKeyword.value = '';
        selectedLetter.value = '';
        if(!allProducts || allProducts.value.length<=0){
            await loadProducts();
        }
        displayedProducts.value = allProducts.value.slice(0, INITIAL_COUNT);
    };

 
    // onMounted(loadProducts);

    return {
     
        allProducts,
        displayedProducts,
        categories,
        loading,
        error,
        selectedLetter,
        searchKeyword,
        letterIndex,
        filteredByLetter,
        currentCount,
        totalCount,
        loadProducts,
        loadMore,
        setLetter,
        setSearch,
        resetToDefault
    };
}